const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

function normalizeVisual(value) {
  if (!isRecord(value) || value.kind !== "rectangle") return null;
  const { x, y, width, height } = value;
  if (![x, y, width, height].every((coordinate) => typeof coordinate === "number" && Number.isFinite(coordinate))) {
    return null;
  }
  if (x < 0 || y < 0 || width <= 0 || height <= 0 || x + width > 100 || y + height > 100) {
    return null;
  }
  return { kind: "rectangle", x, y, width, height };
}

export function getSpacePresentation(space) {
  const presentation = isRecord(space?.presentation) ? space.presentation : {};
  return {
    label: typeof presentation.label === "string" && presentation.label.trim()
      ? presentation.label.trim()
      : space?.name || space?.canonical_name || "Space",
    role: typeof presentation.role === "string" ? presentation.role : "",
    visual: normalizeVisual(presentation.visual),
  };
}

function buildTree(spaces, parentId = null) {
  if (!isRecord(spaces)) return [];

  return Object.entries(spaces).map(([id, source]) => {
    const space = isRecord(source) ? source : {};
    const presentation = isRecord(space.presentation) ? space.presentation : {};
    const dimensions = isRecord(space.dimensions) ? space.dimensions : {};
    const normalizedPresentation = getSpacePresentation(space);

    return {
      id,
      parentId,
      label: normalizedPresentation.label || presentation.label || id,
      type: typeof space.type === "string" ? space.type : "",
      purpose: typeof space.purpose === "string" ? space.purpose : "",
      description: typeof space.description === "string" ? space.description : "",
      tags: Array.isArray(space.tags) ? space.tags.filter((tag) => typeof tag === "string") : [],
      dimensions,
      capabilities: Array.isArray(space.capabilities)
        ? space.capabilities.filter((capability) => typeof capability === "string")
        : [],
      presentation: normalizedPresentation,
      subspaces: buildTree(space.subspaces, id),
    };
  });
}

export function getRootSpaces(spaces) {
  return buildTree(spaces);
}

export function buildSpaceTree(spaces) {
  return getRootSpaces(spaces);
}

export function getSpaceChildren(space) {
  return Array.isArray(space?.subspaces) ? space.subspaces : [];
}

export function flattenSpaces(spaces) {
  const roots = Array.isArray(spaces) ? spaces : getRootSpaces(spaces);
  const flattened = [];

  function visit(nodes, depth, path) {
    for (const space of nodes) {
      const nextPath = [...path, space];
      flattened.push({ ...space, depth, path: nextPath });
      visit(getSpaceChildren(space), depth + 1, nextPath);
    }
  }

  visit(roots, 0, []);
  return flattened;
}

export function getSpaceById(spaces, spaceId) {
  return flattenSpaces(spaces).find((space) => space.id === spaceId) || null;
}

export function getSpacePath(spaces, spaceId) {
  return getSpaceById(spaces, spaceId)?.path || [];
}

export function getSpaceCapabilities(space, capabilityCatalog = {}) {
  if (!Array.isArray(space?.capabilities)) return [];

  return space.capabilities.map((id) => {
    const capability = isRecord(capabilityCatalog[id]) ? capabilityCatalog[id] : {};
    const type = typeof capability.type === "string" ? capability.type : "";
    const configured = [capability.label, capability.name].find((value) => typeof value === "string" && value.trim());
    const fallback = type ? type.replaceAll("_", " ") : "Configured capability";
    return {
      id,
      // A configured label is used verbatim; only the type-derived fallback is capitalised.
      label: configured ? configured.trim() : fallback.charAt(0).toUpperCase() + fallback.slice(1),
      type,
      health: typeof capability.health === "string" ? capability.health : "UNKNOWN",
    };
  });
}

export function getSpaceVisualLayout(spaces) {
  const roots = Array.isArray(spaces) ? spaces : getRootSpaces(spaces);
  const positioned = [];
  const nestedLabelSafeArea = { horizontal: 0.03, top: 0.12, bottom: 0.03 };

  // Visual coordinates are nested percentages, never inferred physical dimensions.
  function visit(nodes, parentBounds, depth) {
    for (const space of nodes) {
      const visual = space.presentation?.visual;
      const safeBounds = depth === 0 ? parentBounds : {
        x: parentBounds.x + parentBounds.width * nestedLabelSafeArea.horizontal,
        y: parentBounds.y + parentBounds.height * nestedLabelSafeArea.top,
        width: parentBounds.width * (1 - nestedLabelSafeArea.horizontal * 2),
        height: parentBounds.height * (1 - nestedLabelSafeArea.top - nestedLabelSafeArea.bottom),
      };
      const bounds = visual
        ? {
            x: safeBounds.x + (safeBounds.width * visual.x) / 100,
            y: safeBounds.y + (safeBounds.height * visual.y) / 100,
            width: (safeBounds.width * visual.width) / 100,
            height: (safeBounds.height * visual.height) / 100,
          }
        : null;
      if (bounds) positioned.push({ ...space, bounds, depth });
      visit(getSpaceChildren(space), bounds || parentBounds, depth + 1);
    }
  }

  visit(roots, { x: 0, y: 0, width: 100, height: 100 }, 0);
  return positioned;
}

export function describeDimensions(dimensions) {
  if (!isRecord(dimensions)) return [];
  const unit = typeof dimensions.unit === "string" ? dimensions.unit : "";
  return ["length", "width", "height"]
    .filter((dimension) => typeof dimensions[dimension] === "number" && Number.isFinite(dimensions[dimension]))
    .map((dimension) => `${dimension}: ${dimensions[dimension]}${unit ? ` ${unit}` : ""}`);
}
